import React from 'react'
import clsx from 'classnames'
import { Helmet } from '@dr.pogodin/react-helmet'
import { useSelector } from 'react-redux'
import type { AnyPage } from 'api/api.types'
import { getIsPageLoading, getPage } from 'src/store/app.state.store'
import { getSettings } from 'src/store/data.store'
import { useNavigationContext } from 'components/Utility/NavigationContext/NavigationContext'
import { Redirect } from 'components/Utility/Redirect/Redirect'
import { PageNotFound } from 'components/PageElements/PageNotFound/PageNotFound'
import { Loader } from 'components/Utility/Loader/Loader'
import { Footer } from 'components/PageElements/Footer/Footer'
import { Cookie } from 'components/PageElements/Cookie/Cookie'
import { MainPage } from 'templates/MainPage/MainPage'
import { NewsPage } from 'templates/NewsPage/NewsPage'
import { MobilePage } from 'templates/MobilePage/MobilePage'
import { GiftCardsPage } from 'templates/GiftCardsPage/GiftCardsPage'
import { ContactsPage } from 'templates/ContactsPage/ContactsPage'
import { AboutPage } from 'templates/AboutPage/AboutPage'
import { SalonPage } from 'templates/SalonPage/SalonPage'
import { NewsItemPage } from 'templates/NewsItemPage/NewsItemPage'
import { ServicePage } from 'templates/ServicePage/ServicePage'
import { ClubPage } from 'templates/ClubPage/ClubPage'
import { PromoPage } from 'templates/PromoPage/PromoPage'
import { TextPage } from 'templates/TextPage/TextPage'
import { SalonsListPage } from 'templates/SalonsListPage/SalonsListPage'

const renderTemplate = (page: AnyPage): React.ReactNode => {
  switch (page.template) {
    case 'main-page':
      return <MainPage page={page} />
    case 'news-list':
      return <NewsPage page={page} />
    case 'promotions-list':
      return <NewsPage page={page} />
    case 'mobile-page':
      return <MobilePage page={page} />
    case 'contacts-page':
      return <ContactsPage page={page} />
    case 'about-page':
      return <AboutPage page={page} />
    case 'gift-card-page':
      return <GiftCardsPage page={page} />
    case 'salon-page':
      return <SalonPage page={page} />
    case 'salons-list':
      return <SalonsListPage page={page} />
    case 'service-page':
      return <ServicePage page={page} />
    case 'news-page':
      return <NewsItemPage page={page} />
    case 'promotion-page':
      return <NewsItemPage page={page} />
    case 'club-page':
      return <ClubPage page={page} />
    case 'promo-page':
      return <PromoPage page={page} />
    case 'text-page':
      return <TextPage page={page} />
    default:
      return null
  }
}

/** Canonical URL: no trailing slash, no query string */
const makeCanonical = (domain: string, fullPath: string) => {
  return 'https://' + `${domain}/${fullPath}`
    .replaceAll('//', '/')
    .replace(/\/$/, '')
    .replace(/(\?|&)([^=]+)=?([^&]+)/g, '')
}

export const PageContent: React.FC = () => {
  const loading = useSelector(getIsPageLoading)
  const page = useSelector(getPage)
  const settings = useSelector(getSettings)
  const navigationContext = useNavigationContext()

  if (loading) {
    return <Loader />
  }
  if (!page || !settings) {
    return <PageNotFound />
  }
  if (page.template === 'redirect') {
    return <Redirect status={page.status} to={page.to} />
  }
  if (page.template === undefined) {
    return <PageNotFound />
  }

  const content = renderTemplate(page)
  if (!content) {
    return <PageNotFound />
  }

  const href = navigationContext.href ?? page.fullPath
  const origin = navigationContext.origin ?? ''
  const seo = page.templateVars.seo
  const title = page.name || 'Страница не найдена'

  return <div className={clsx(['page-content', { loading }])}>
    <Helmet>
      <title>{title}</title>
      <meta name={'description'} content={seo?.description} />
      <meta name={'keywords'} content={seo?.keywords} />
      {page.noIndex && (
        <meta name="robots" content="noindex, nofollow" />
      )}
      <meta property={'og:site_name'} content="Пальчики" />
      <meta property={'og:title'} content={title} />
      <meta property={'og:type'} content={'website'} />
      <meta property={'og:description'} content={seo?.description} />
      <meta property={'og:url'} content={href} />
      <meta property={'og:image'} content={`${origin}/static/share-image.png`} />
      <meta property={'og:image:width'} content="1200" />
      <meta property={'og:image:height'} content="1200" />
      <link rel={'preconnect'} href={'https://api-maps.yandex.ru'} />
      <link rel={'preconnect'} href={'https://www.google-analytics.com'} />
      <link rel={'canonical'} href={makeCanonical(settings.domain, page.fullPath)} />
    </Helmet>
    {content}
    <Footer noSeasons={page.template === 'salon-page'} />
    <Cookie />
  </div>
}
